import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuth";
import { useSubscriptionFeatures } from "@/hooks/useSubscriptionFeatures";
import { useOrganizationManagement, type OrgMember } from "@/hooks/useOrganizationManagement";
import { useRealtimeOrganizationData } from "@/hooks/useRealtimeOrganizationData";
import { useEmailValidation } from "@/hooks/useEmailValidation";
import { toast } from "@/hooks/use-toast";

export const TeamManagement = () => {
  const { user } = useAuth();
  const { organizationData, canAddMoreSeats, refreshOrganizationData } = useSubscriptionFeatures();
  const { inviteMember, fetchMembers, fetchPendingInvitations, updateMemberRole, removeMember, cancelInvitation } = useOrganizationManagement();
  
  const [initialMembers, setInitialMembers] = useState<OrgMember[]>([]);
  const [initialInvitations, setInitialInvitations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Utiliser le hook temps réel
  const { members, invitations: pendingInvitations, setInvitations: setPendingInvitations } = useRealtimeOrganizationData({
    organizationId: organizationData?.id,
    initialMembers,
    initialInvitations
  });
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const [inviteLoading, setInviteLoading] = useState(false);
  
  // Validation email temps réel avec vérification doublons
  const emailValidation = useEmailValidation(inviteEmail, {
    organizationId: organizationData?.id,
    checkDuplicates: true,
    debounceMs: 300
  });

  const [confirmRemoveDialog, setConfirmRemoveDialog] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{id: string, name: string, email: string} | null>(null);

  const loadMembers = async () => {
    if (!organizationData?.id) return;
    
    setLoading(true);
    const [membersResult, invitationsResult] = await Promise.all([
      fetchMembers(organizationData.id),
      fetchPendingInvitations(organizationData.id)
    ]);
    
    if (membersResult.error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les membres de l'équipe",
        variant: "destructive",
      });
    } else {
      setInitialMembers(membersResult.data);
    }
    
    if (invitationsResult.error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les invitations",
        variant: "destructive",
      });
    } else {
      setInitialInvitations(invitationsResult.data);
    }
    
    setLoading(false);
  };

  useEffect(() => {
    loadMembers();
  }, [organizationData?.id]);

  const handleInvite = async () => {
    if (!organizationData?.id || !inviteEmail.trim() || !inviteFirstName.trim() || !inviteLastName.trim()) return;

    // Validation finale avant envoi
    if (!emailValidation.isValid) {
      toast({
        title: "Email invalide",
        description: emailValidation.error || "Veuillez corriger l'adresse email",
        variant: "destructive",
      });
      return;
    }

    if (!canAddMoreSeats) {
      toast({
        title: "Limite atteinte",
        description: "Vous avez atteint la limite de sièges de votre plan",
        variant: "destructive",
      });
      return;
    }

    setInviteLoading(true);
    const { error } = await inviteMember(
      organizationData.id, 
      inviteEmail.trim(), 
      inviteRole,
      inviteFirstName.trim(),
      inviteLastName.trim()
    );
    
    if (error) {
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer l'invitation",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Invitation envoyée",
        description: `Une invitation a été envoyée à ${inviteFirstName} ${inviteLastName}`,
      });
      setInviteDialogOpen(false);
      setInviteEmail("");
      setInviteFirstName("");
      setInviteLastName("");
      setInviteRole("member");
      await loadMembers(); // Cela va maintenant recharger les membres ET les invitations
      await refreshOrganizationData();
    }
    setInviteLoading(false);
  };

  const handleRoleUpdate = async (userId: string, newRole: "admin" | "member") => {
    if (!organizationData?.id) return;

    const { error } = await updateMemberRole(organizationData.id, userId, newRole);
    if (error) {
      toast({
        title: "Erreur",
        description: "Impossible de modifier le rôle",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Rôle modifié",
        description: "Le rôle du membre a été mis à jour",
      });
      await loadMembers();
    }
  };

  const handleRemoveMember = async (userId: string) => {
    // Trouver le membre à supprimer pour afficher ses infos dans la popup
    const member = allMembers.find(m => m.user_id === userId);
    if (member) {
      setMemberToRemove({
        id: userId,
        name: member.profile ? `${member.profile.first_name} ${member.profile.last_name}` : 'Membre',
        email: member.profile?.email || 'Email non disponible'
      });
      setConfirmRemoveDialog(true);
    }
  };

  const confirmRemoveMember = async () => {
    if (!organizationData?.id || !memberToRemove) return;

    const { error } = await removeMember(organizationData.id, memberToRemove.id);
    if (error) {
      toast({
        title: "Erreur",
        description: "Impossible de retirer le membre",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Membre retiré",
        description: `${memberToRemove.name} a été retiré de l'organisation`,
      });
      await loadMembers();
      await refreshOrganizationData();
    }
    
    setConfirmRemoveDialog(false);
    setMemberToRemove(null);
  };

  const handleCancelInvitation = async (invitationId: string, email: string) => {
    console.log("🔄 Tentative d'annulation de l'invitation:", invitationId, "pour:", email);
    
    const { error } = await cancelInvitation(invitationId);
    
    if (error) {
      console.error("❌ Erreur lors de l'annulation:", error);
      toast({
        title: "Erreur",
        description: `Impossible d'annuler l'invitation: ${error.message}`,
        variant: "destructive",
      });
    } else {
      console.log("✅ Invitation annulée avec succès");
      toast({
        title: "Invitation annulée",
        description: `L'invitation pour ${email} a été annulée`,
      });
      
      // Attendre un peu pour laisser le temps à la suppression de se propager
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Recharger spécifiquement les invitations
      if (organizationData?.id) {
        console.log("🔄 Rechargement des invitations pour l'organisation:", organizationData.id);
        
        const { data: newInvitations, error: refreshError } = await fetchPendingInvitations(organizationData.id);
        
        console.log("📊 Résultat rechargement invitations:", { 
          newInvitations: newInvitations?.length || 0, 
          error: refreshError,
          invitations: newInvitations 
        });
        
        if (!refreshError) {
          setPendingInvitations(newInvitations as any[]);
          console.log("✅ État des invitations mis à jour:", newInvitations.length, "invitation(s)");
        } else {
          console.error("❌ Erreur lors du rechargement des invitations:", refreshError);
        }
      } else {
        console.error("❌ Pas d'ID d'organisation disponible pour le rechargement");
      }
      
      // Forcer un rechargement complet comme fallback
      await loadMembers();
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case "owner":
        return "Propriétaire";
      case "admin":
        return "Administrateur";
      default:
        return "Membre";
    }
  };



  if (!organizationData) {
    return (
      <div className="size-lf-stretch px-6 inline-flex flex-col justify-start items-end gap-5">
        <div className="w-full text-center py-8 text-neutral-400">
          Aucune organisation trouvée
        </div>
      </div>
    );
  }

  const activeMembers = members.filter(m => m.status === 'active');
  
  const allMembers = [...activeMembers, ...pendingInvitations.map(inv => ({
    id: inv.id,
    profile: { first_name: inv.email.split('@')[0], last_name: '', email: inv.email },
    role: inv.role,
    status: 'invited',
    activated_at: inv.created_at,
    user_id: null
  }))];

  return (
    <div className="self-stretch px-6 flex flex-col justify-start items-start gap-5">
      {/* Statistiques en haut */}
      <div className="w-full h-24 flex justify-start items-center gap-5">
        <div className="flex-1 p-5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-zinc-200 flex justify-start items-center gap-3">
          <div className="size- p-2.5 bg-teal-400/10 rounded-xl inline-flex flex-col justify-start items-start gap-2.5 overflow-hidden">
            <div data-svg-wrapper className="relative">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.37 2.14984L21.37 5.74982C21.72 5.88982 22 6.30981 22 6.67981V9.99982C22 10.5498 21.55 10.9998 21 10.9998H3C2.45 10.9998 2 10.5498 2 9.99982V6.67981C2 6.30981 2.28 5.88982 2.63 5.74982L11.63 2.14984C11.83 2.06984 12.17 2.06984 12.37 2.14984Z" stroke="#40E0D0" strokeWidth="1.8" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 22H2V19C2 18.45 2.45 18 3 18H21C21.55 18 22 18.45 22 19V22Z" stroke="#40E0D0" strokeWidth="1.8" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4 18V11" stroke="#40E0D0" strokeWidth="1.8" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 18V11" stroke="#40E0D0" strokeWidth="1.8" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 18V11" stroke="#40E0D0" strokeWidth="1.8" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 18V11" stroke="#40E0D0" strokeWidth="1.8" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20 18V11" stroke="#40E0D0" strokeWidth="1.8" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M1 22H23" stroke="#40E0D0" strokeWidth="1.8" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 8.5C12.8284 8.5 13.5 7.82843 13.5 7C13.5 6.17157 12.8284 5.5 12 5.5C11.1716 5.5 10.5 6.17157 10.5 7C10.5 7.82843 11.1716 8.5 12 8.5Z" stroke="#40E0D0" strokeWidth="1.8" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          <div className="size- inline-flex flex-col justify-start items-start gap-1">
            <div className="justify-start text-gray-800 text-2xl font-semibold font-['Bricolage_Grotesque']">{organizationData.name}</div>
            <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque'] capitalize">Organisation</div>
          </div>
        </div>
        <div className="flex-1 p-5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-zinc-200 flex justify-start items-center gap-3">
          <div className="size- p-2.5 bg-green-500/10 rounded-xl inline-flex flex-col justify-start items-start gap-2.5 overflow-hidden">
            <div data-svg-wrapper className="relative">
              <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.3335 8.27V4.23C22.3335 2.64 21.6935 2 20.1035 2H16.0635C14.4735 2 13.8335 2.64 13.8335 4.23V8.27C13.8335 9.86 14.4735 10.5 16.0635 10.5H20.1035C21.6935 10.5 22.3335 9.86 22.3335 8.27Z" stroke="#32CD32" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10.8335 8.52V3.98C10.8335 2.57 10.1935 2 8.6035 2H4.5635C2.9735 2 2.3335 2.57 2.3335 3.98V8.51C2.3335 9.93 2.9735 10.49 4.5635 10.49H8.6035C10.1935 10.5 10.8335 9.93 10.8335 8.52Z" stroke="#32CD32" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10.8335 19.77V15.73C10.8335 14.14 10.1935 13.5 8.6035 13.5H4.5635C2.9735 13.5 2.3335 14.14 2.3335 15.73V19.77C2.3335 21.36 2.9735 22 4.5635 22H8.6035C10.1935 22 10.8335 21.36 10.8335 19.77Z" stroke="#32CD32" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15.3335 15.5H21.3335" stroke="#32CD32" strokeWidth="1.8" strokeLinecap="round"/>
                <path d="M15.3335 19.5H21.3335" stroke="#32CD32" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </div>
          </div>
          <div className="size- inline-flex flex-col justify-start items-start gap-1">
            <div className="justify-start text-gray-800 text-2xl font-semibold font-['Bricolage_Grotesque']">{organizationData.usedSeats}/{organizationData.features.totalSeats}</div>
            <div className="justify-start"><span className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque'] uppercase">S</span><span className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque'] lowercase">ièges utilisés</span></div>
          </div>
        </div>
        <div className="flex-1 p-5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-zinc-200 flex justify-start items-center gap-3">
          <div className="size-11 p-2.5 bg-yellow-400/10 rounded-xl inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden">
            <div data-svg-wrapper className="relative">
              <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.6666 7.16C18.6066 7.15 18.5366 7.15 18.4766 7.16C17.0966 7.11 15.9966 5.98 15.9966 4.58C15.9966 3.15 17.1466 2 18.5766 2C20.0066 2 21.1566 3.16 21.1566 4.58C21.1466 5.98 20.0466 7.11 18.6666 7.16Z" stroke="#FFC506" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17.6364 14.4402C19.0064 14.6702 20.5165 14.4302 21.5765 13.7202C22.9865 12.7802 22.9865 11.2402 21.5765 10.3002C20.5065 9.59016 18.9764 9.35016 17.6064 9.59016" stroke="#FFC506" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6.63649 7.16C6.69649 7.15 6.76648 7.15 6.82648 7.16C8.20648 7.11 9.30648 5.98 9.30648 4.58C9.30648 3.15 8.15649 2 6.72649 2C5.29649 2 4.14648 3.16 4.14648 4.58C4.15648 5.98 5.25649 7.11 6.63649 7.16Z" stroke="#FFC506" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7.66644 14.4402C6.29644 14.6702 4.78645 14.4302 3.72645 13.7202C2.31645 12.7802 2.31645 11.2402 3.72645 10.3002C4.79645 9.59016 6.32644 9.35016 7.69644 9.59016" stroke="#FFC506" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12.6666 14.6302C12.6066 14.6202 12.5366 14.6202 12.4766 14.6302C11.0966 14.5802 9.99658 13.4502 9.99658 12.0502C9.99658 10.6202 11.1466 9.47021 12.5766 9.47021C14.0066 9.47021 15.1566 10.6302 15.1566 12.0502C15.1466 13.4502 14.0466 14.5902 12.6666 14.6302Z" stroke="#FFC506" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9.75672 17.7804C8.34672 18.7204 8.34672 20.2603 9.75672 21.2003C11.3567 22.2703 13.9767 22.2703 15.5767 21.2003C16.9867 20.2603 16.9867 18.7204 15.5767 17.7804C13.9867 16.7204 11.3567 16.7204 9.75672 17.7804Z" stroke="#FFC506" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          <div className="size- inline-flex flex-col justify-start items-start gap-1">
            <div className="justify-start text-gray-800 text-2xl font-semibold font-['Bricolage_Grotesque'] tracking-tight">{activeMembers.length}</div>
            <div className="justify-start"><span className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque'] uppercase">M</span><span className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque'] lowercase">embres actifs</span></div>
          </div>
        </div>
      </div>

      {/* En-tête avec titre et bouton d'invitation */}
      <div className="w-full h-12 flex justify-between items-center">
        <div className="size- inline-flex flex-col justify-start items-start gap-1">
          <div className="self-stretch justify-start text-gray-800 text-2xl font-medium font-['Bricolage_Grotesque']">Gestion d'équipe</div>
          <div className="justify-center text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">Invitez et gérez les membres de votre organisation</div>
        </div>
        
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogTrigger asChild>
            <div className="h-10 pl-2.5 pr-3 py-3 bg-teal-400 rounded-[10px] flex justify-center items-center gap-1 overflow-hidden cursor-pointer" style={{ opacity: !canAddMoreSeats ? 0.5 : 1, pointerEvents: !canAddMoreSeats ? 'none' : 'auto' }}>
              <div data-svg-wrapper className="relative">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.3335 8.00016H8.00016M8.00016 8.00016H12.6668M8.00016 8.00016V3.3335M8.00016 8.00016V12.6668" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="justify-center text-white text-sm font-semibold font-['Bricolage_Grotesque']">Inviter un membre</div>
            </div>
          </DialogTrigger>
          <DialogContent className="w-[573px] p-6 bg-white rounded-3xl">
            <div className="size- flex flex-col justify-start items-start gap-1">
              <div className="justify-start text-gray-800 text-2xl font-medium font-['Bricolage_Grotesque']">Inviter un nouveau membre</div>
              <div className="justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">Envoyez une invitation par email pour ajouter un membre à votre organisation.</div>
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-4">
              <div className="self-stretch flex flex-col justify-start items-start gap-2">
                <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">Prénom</div>
                <div className="self-stretch h-11 px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20">
                  <input
                    type="text"
                    placeholder="Jean"
                    value={inviteFirstName}
                    onChange={(e) => setInviteFirstName(e.target.value)}
                    className="w-full bg-transparent text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] placeholder:text-neutral-400 outline-none"
                  />
                </div>
              </div>
              
              <div className="self-stretch flex flex-col justify-start items-start gap-2">
                <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">Nom</div>
                <div className="self-stretch h-11 px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20">
                  <input
                    type="text"
                    placeholder="Dupont"
                    value={inviteLastName}
                    onChange={(e) => setInviteLastName(e.target.value)}
                    className="w-full bg-transparent text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] placeholder:text-neutral-400 outline-none"
                  />
                </div>
              </div>
              
              <div className="self-stretch flex flex-col justify-start items-start gap-2">
                <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">Adresse email</div>
                <div className={`self-stretch h-11 px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] inline-flex justify-between items-center ${
                  emailValidation.error 
                    ? 'outline-red-500' 
                    : emailValidation.isValid && inviteEmail.trim() 
                      ? 'outline-green-500' 
                      : 'outline-neutral-400/20'
                }`}>
                  <input
                    type="email"
                    placeholder="exemple@entreprise.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="flex-1 bg-transparent text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] placeholder:text-neutral-400 outline-none"
                  />
                  <div className="flex items-center gap-2">
                    {emailValidation.isChecking && (
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    )}
                    {emailValidation.isValid && inviteEmail.trim() && !emailValidation.isChecking && (
                      <div className="text-green-500">✓</div>
                    )}
                    {emailValidation.error && !emailValidation.isChecking && (
                      <div className="text-red-500">✗</div>
                    )}
                  </div>
                </div>
                
                {/* Messages de validation */}
                {emailValidation.isChecking && (
                  <div className="text-blue-500 text-xs font-medium font-['Bricolage_Grotesque']">
                    Vérification de l'adresse email...
                  </div>
                )}
                
                {emailValidation.error && !emailValidation.isChecking && (
                  <div className="text-red-500 text-xs font-medium font-['Bricolage_Grotesque']">
                    {emailValidation.error}
                  </div>
                )}
                
                {emailValidation.isValid && inviteEmail.trim() && !emailValidation.isChecking && (
                  <div className="text-green-500 text-xs font-medium font-['Bricolage_Grotesque']">
                    ✓ Adresse email valide
                  </div>
                )}
                
                {emailValidation.warnings.length > 0 && (
                  <div className="text-yellow-600 text-xs font-medium font-['Bricolage_Grotesque']">
                    {emailValidation.warnings.join(', ')}
                  </div>
                )}
              </div>
              <div className="self-stretch h-16 flex flex-col justify-start items-start gap-2">
                <div className="self-stretch justify-center text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">Rôle</div>
                <Select value={inviteRole} onValueChange={(value: "admin" | "member") => setInviteRole(value)}>
                  <SelectTrigger className="self-stretch h-11 px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 border-none [&>svg]:w-5 [&>svg]:h-5 [&>svg]:stroke-[#8F949D]">
                    <SelectValue placeholder="Membre" className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Membre</SelectItem>
                    <SelectItem value="admin">Administrateur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="self-stretch inline-flex justify-end items-center gap-3">
              <div className="h-10 p-3 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex justify-start items-center gap-1 overflow-hidden cursor-pointer" onClick={() => setInviteDialogOpen(false)}>
                <div className="justify-center text-neutral-400 text-sm font-semibold font-['Bricolage_Grotesque']">Annuler</div>
              </div>
              <div 
                className="h-10 p-3 bg-teal-400 rounded-[10px] flex justify-start items-center gap-1 overflow-hidden cursor-pointer" 
                onClick={handleInvite} 
                style={{ 
                  opacity: inviteLoading || !emailValidation.isValid || emailValidation.isChecking || !inviteFirstName.trim() || !inviteLastName.trim() ? 0.5 : 1, 
                  pointerEvents: inviteLoading || !emailValidation.isValid || emailValidation.isChecking || !inviteFirstName.trim() || !inviteLastName.trim() ? 'none' : 'auto' 
                }}
              >
                <div className="justify-center text-white text-sm font-semibold font-['Bricolage_Grotesque']">
                  {inviteLoading ? "Envoi..." : "Envoyer l'invitation"}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tableau des membres */}
      <div className="w-full bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex justify-start items-start overflow-hidden">
        <div className="w-56 self-stretch inline-flex flex-col justify-start items-start">
          <div className="self-stretch h-14 p-5 border-b border-neutral-400/20 inline-flex justify-start items-center gap-1.5">
            <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque'] leading-none">Membre</div>
          </div>
          {loading ? (
            <div className="self-stretch h-14 p-5 inline-flex justify-start items-center gap-4">
              <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">Chargement...</div>
            </div>
          ) : allMembers.length === 0 ? (
            <div className="self-stretch h-14 p-5 inline-flex justify-start items-center gap-4">
              <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">Aucun membre</div>
            </div>
          ) : (
            allMembers.map((member, index) => (
              <div key={member.id || index} className="self-stretch h-14 p-5 inline-flex justify-start items-center gap-4">
                <div className="size- inline-flex flex-col justify-center items-start">
                  <div className="justify-start text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] capitalize">
                    {member.profile ? `${member.profile.first_name} ${member.profile.last_name}` : 'Nom non disponible'}
                  </div>
                  <div className="justify-start text-neutral-400 text-xs font-medium font-['Bricolage_Grotesque'] lowercase">
                    {member.profile?.email || 'Email non disponible'}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        
        <div className="flex-1 self-stretch inline-flex flex-col justify-start items-start">
          <div className="self-stretch h-14 p-5 border-b border-neutral-400/20 inline-flex justify-start items-center gap-1.5">
            <div className="size- flex justify-start items-center gap-1">
              <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque'] leading-none">Rôle</div>
            </div>
          </div>
          {loading ? (
            <div className="self-stretch h-14 p-5 inline-flex justify-start items-center gap-4">
              <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">-</div>
            </div>
          ) : allMembers.length === 0 ? (
            <div className="self-stretch h-14 p-5 inline-flex justify-start items-center gap-4">
              <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">-</div>
            </div>
          ) : (
            allMembers.map((member, index) => (
              <div key={member.id || index} className="self-stretch h-14 p-5 inline-flex justify-start items-center gap-4">
                <div className="justify-start text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] capitalize">
                  {getRoleLabel(member.role)}
                </div>
              </div>
            ))
          )}
        </div>
        
        <div className="flex-1 self-stretch inline-flex flex-col justify-start items-start">
          <div className="self-stretch h-14 p-5 border-b border-neutral-400/20 inline-flex justify-start items-center gap-1.5">
            <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque'] leading-none">Statut</div>
          </div>
          {loading ? (
            <div className="self-stretch h-14 p-5 inline-flex justify-start items-center gap-4">
              <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">-</div>
            </div>
          ) : allMembers.length === 0 ? (
            <div className="self-stretch h-14 p-5 inline-flex justify-start items-center gap-4">
              <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">-</div>
            </div>
          ) : (
            allMembers.map((member, index) => (
              <div key={member.id || index} className="self-stretch h-14 p-5 inline-flex justify-start items-center gap-4">
                <div className="size- px-2 py-[5px] rounded-md outline outline-1 outline-offset-[-1px] outline-teal-400 flex justify-center items-center gap-2.5">
                  <div className="justify-start text-teal-400 text-xs font-medium font-['Bricolage_Grotesque'] capitalize">
                    {member.status === 'active' ? 'Actif' : member.status === 'invited' ? 'Invité' : 'Actif'}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        
        <div className="flex-1 self-stretch inline-flex flex-col justify-start items-start">
          <div className="self-stretch h-14 p-5 border-b border-neutral-400/20 inline-flex justify-start items-center gap-4">
            <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque'] leading-none">Rejoint le</div>
          </div>
          {loading ? (
            <div className="self-stretch h-14 p-5 inline-flex justify-start items-center gap-4">
              <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">-</div>
            </div>
          ) : allMembers.length === 0 ? (
            <div className="self-stretch h-14 p-5 inline-flex justify-start items-center gap-4">
              <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">-</div>
            </div>
          ) : (
            allMembers.map((member, index) => (
              <div key={member.id || index} className="self-stretch h-14 p-5 inline-flex justify-start items-center gap-4">
                <div className="justify-start text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
                  {member.activated_at 
                    ? new Date(member.activated_at).toLocaleDateString('fr-FR')
                    : '26/08/2025'
                  }
                </div>
              </div>
            ))
          )}
        </div>
        
        <div className="self-stretch inline-flex flex-col justify-start items-start">
          <div className="self-stretch h-14 p-5 border-b border-neutral-400/20 inline-flex justify-start items-center gap-4">
            <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque'] leading-none">Action</div>
          </div>
          {loading ? (
            <div className="h-14 p-5 inline-flex justify-start items-center gap-4">
              <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">-</div>
            </div>
          ) : allMembers.length === 0 ? (
            <div className="h-14 p-5 inline-flex justify-start items-center gap-4">
              <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">-</div>
            </div>
          ) : (
            allMembers.map((member, index) => (
              <div key={member.id || index} className="h-14 p-5 inline-flex justify-start items-center gap-4">
                {member.user_id !== user?.id && member.role !== 'owner' ? (
                  <div className="size- flex justify-start items-center gap-2">
                    {member.status === 'invited' ? (
                      <>
                        <div className="size- flex justify-start items-center gap-1 cursor-pointer" onClick={() => handleCancelInvitation(member.id, member.profile?.email || '')}>
                          <div data-svg-wrapper className="relative">
                            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8.00016 15.1668C11.6802 15.1668 14.6668 12.1802 14.6668 8.50016C14.6668 4.82016 11.6802 1.8335 8.00016 1.8335C4.32016 1.8335 1.3335 4.82016 1.3335 8.50016C1.3335 12.1802 4.32016 15.1668 8.00016 15.1668Z" stroke="#FFC506" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M12.5999 3.8335L3.2666 13.1668" stroke="#FFC506" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <div className="justify-start text-neutral-400 text-xs font-medium font-['Bricolage_Grotesque']">Annuler</div>
                        </div>
                        <div className="w-3 h-0 origin-top-left rotate-90 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>
                        <div className="size- flex justify-start items-center gap-1">
                          <div data-svg-wrapper className="relative">
                            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11.3335 14.1668H4.66683C2.66683 14.1668 1.3335 13.1668 1.3335 10.8335V6.16683C1.3335 3.8335 2.66683 2.8335 4.66683 2.8335H11.3335C13.3335 2.8335 14.6668 3.8335 14.6668 6.16683V10.8335C14.6668 13.1668 13.3335 14.1668 11.3335 14.1668Z" stroke="#40E0D0" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M11.3332 6.5L9.2465 8.16667C8.55983 8.71333 7.43317 8.71333 6.7465 8.16667L4.6665 6.5" stroke="#40E0D0" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <div className="justify-start text-neutral-400 text-xs font-medium font-['Bricolage_Grotesque']">Renvoyer l'email</div>
                        </div>
                        <div className="w-3 h-0 origin-top-left rotate-90 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>
                        <div className="size- flex justify-start items-center gap-1">
                          <div data-svg-wrapper className="relative">
                            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8.10671 7.74683C8.04004 7.74016 7.96004 7.74016 7.88671 7.74683C6.30004 7.6935 5.04004 6.3935 5.04004 4.7935C5.04004 3.16016 6.36004 1.8335 8.00004 1.8335C9.63337 1.8335 10.96 3.16016 10.96 4.7935C10.9534 6.3935 9.69337 7.6935 8.10671 7.74683Z" stroke="#FF7F50" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M4.77348 10.2065C3.16014 11.2865 3.16014 13.0465 4.77348 14.1198C6.60681 15.3465 9.61348 15.3465 11.4468 14.1198C13.0601 13.0398 13.0601 11.2798 11.4468 10.2065C9.62014 8.9865 6.61348 8.9865 4.77348 10.2065Z" stroke="#FF7F50" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <div className="justify-start text-neutral-400 text-xs font-medium font-['Bricolage_Grotesque']">Rôle</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="size- flex justify-start items-center gap-1 cursor-pointer" onClick={() => handleRemoveMember(member.user_id!)}>
                          <div data-svg-wrapper className="relative">
                            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8.00016 15.1668C11.6802 15.1668 14.6668 12.1802 14.6668 8.50016C14.6668 4.82016 11.6802 1.8335 8.00016 1.8335C4.32016 1.8335 1.3335 4.82016 1.3335 8.50016C1.3335 12.1802 4.32016 15.1668 8.00016 15.1668Z" stroke="#FFC506" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M12.5999 3.8335L3.2666 13.1668" stroke="#FFC506" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <div className="justify-start text-neutral-400 text-xs font-medium font-['Bricolage_Grotesque']">Annuler</div>
                        </div>
                        <div className="w-3 h-0 origin-top-left rotate-90 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>
                        <div className="size- flex justify-start items-center gap-1">
                          <div data-svg-wrapper className="relative">
                            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11.3335 14.1668H4.66683C2.66683 14.1668 1.3335 13.1668 1.3335 10.8335V6.16683C1.3335 3.8335 2.66683 2.8335 4.66683 2.8335H11.3335C13.3335 2.8335 14.6668 3.8335 14.6668 6.16683V10.8335C14.6668 13.1668 13.3335 14.1668 11.3335 14.1668Z" stroke="#40E0D0" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M11.3332 6.5L9.2465 8.16667C8.55983 8.71333 7.43317 8.71333 6.7465 8.16667L4.6665 6.5" stroke="#40E0D0" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <div className="justify-start text-neutral-400 text-xs font-medium font-['Bricolage_Grotesque']">Renvoyer l'email</div>
                        </div>
                        <div className="w-3 h-0 origin-top-left rotate-90 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>
                        <div className="size- flex justify-start items-center gap-1 cursor-pointer" onClick={() => handleRoleUpdate(member.user_id!, member.role === 'admin' ? 'member' : 'admin')}>
                          <div data-svg-wrapper className="relative">
                            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8.10671 7.74683C8.04004 7.74016 7.96004 7.74016 7.88671 7.74683C6.30004 7.6935 5.04004 6.3935 5.04004 4.7935C5.04004 3.16016 6.36004 1.8335 8.00004 1.8335C9.63337 1.8335 10.96 3.16016 10.96 4.7935C10.9534 6.3935 9.69337 7.6935 8.10671 7.74683Z" stroke="#FF7F50" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M4.77348 10.2065C3.16014 11.2865 3.16014 13.0465 4.77348 14.1198C6.60681 15.3465 9.61348 15.3465 11.4468 14.1198C13.0601 13.0398 13.0601 11.2798 11.4468 10.2065C9.62014 8.9865 6.61348 8.9865 4.77348 10.2065Z" stroke="#FF7F50" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <div className="justify-start text-neutral-400 text-xs font-medium font-['Bricolage_Grotesque']">Rôle</div>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="justify-start text-neutral-400 text-xs font-medium font-['Bricolage_Grotesque']">-</div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* AlertDialog de confirmation pour la suppression */}
      <AlertDialog open={confirmRemoveDialog} onOpenChange={setConfirmRemoveDialog}>
        <AlertDialogContent className="w-[400px] p-6 bg-white rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800 text-xl font-semibold font-['Bricolage_Grotesque']">
              Confirmer la suppression
            </AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
              Êtes-vous sûr de vouloir retirer <strong>{memberToRemove?.name}</strong> ({memberToRemove?.email}) de l'organisation ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex justify-end gap-3">
            <AlertDialogCancel className="h-10 p-3 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 bg-transparent border-none hover:bg-gray-50">
              <span className="text-neutral-400 text-sm font-semibold font-['Bricolage_Grotesque']">Annuler</span>
            </AlertDialogCancel>
            <AlertDialogAction 
              className="h-10 p-3 bg-red-500 hover:bg-red-600 rounded-[10px] border-none"
              onClick={confirmRemoveMember}
            >
              <span className="text-white text-sm font-semibold font-['Bricolage_Grotesque']">Confirmer la suppression</span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};